import React from 'react'
import PropTypes from 'prop-types'
import glamorous from 'glamorous'
import clientData from 'utils/client-data'
import { generatePpshopsUrl } from 'utils/generate-urls'
import { css } from 'glamor'
import {
  HeadingText,
  BodyText,
  CaptionText,
  Divider,
  Button,
  IconButton,
  V2ChatIcon,
  V2ShareIosIcon,
  V2ShippingSendIcon,
  v2grey600,
} from '@paypalcorp/pp-react'
import { tracking } from 'lib/analytics'
import getContent from 'pp-react-l10n'
import Events from 'lib/analytics/event-tags'
import ProductCoverPhoto from '../shared/components/product-cover-photo'
import { getFormattedAmount } from '../../shared/amount-utils'

import { phoneLandscapeMaxAndSmaller } from 'lib/styles/media-queries'

const l10n = getContent('pages/shops/product-detail')

const Container = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

const ContentBlock = glamorous.div({
  width: '100%',
  marginTop: '20px',
  [phoneLandscapeMaxAndSmaller]: {
    width: '100%',
  },
})

const ProductTitle = css({
  marginBottom: '5px',
  overflowWrap: 'break-word',
})

const ProductDescription = css({
  marginBottom: '24px',
})

const DividerCss = css({
  margin: '24px 0',
})

const ShippingFee = glamorous.div({
  marginTop: '24px',
  color: v2grey600,
  display: 'flex',
  alignItems: 'center',
})

const ShopActions = glamorous.div({
  marginTop: '36px',
  display: 'flex',
  justifyContent: 'space-around',
})

const ProductActions = glamorous.div({
  marginTop: '20px',
  display: 'flex',
  justifyContent: 'center',
})

const shopActionButton = {
  width: '100%',
  height: '48px',
  padding: 0,
  margin: '0 16px',
}

const customMarginLeft = {
  marginLeft: '8px',
}

const customMarginRight = {
  marginRight: '8px',
}

const productActionButton = css({
  margin: '0 8px',
})

ProductDetail.propTypes = {
  product: PropTypes.object,
  productId: PropTypes.string,
  shopId: PropTypes.string,
  isUserLoggedIn: PropTypes.bool,
}

function ProductDetail(props) {
  tracking(Events.public_identity_buyer_item_screen_shown())
  const { isUserLoggedIn, encryptedFlowData = '' } = clientData
  const checkoutUrlForGuest = generatePpshopsUrl(true, encryptedFlowData)
  const checkoutUrlForLoggedInUser = generatePpshopsUrl(
    false,
    encryptedFlowData,
  )
  const { product, slug } = clientData.shopProduct.product

  if (product.hasOwnProperty('errors')) {
    props.history.push({
      pathname: `/seller/error`,
      state: {
        error: {
          headerKey: 'productError',
          subHeaderKey: 'productError',
          ctaButtonKey: 'productError',
          ctaButtonClick: props.history.push({
            pathname: `/${slug}`,
          }),
        },
      },
    })
  }

  const advanceToPayWithPaypalFlow = e => {
    e.preventDefault()
    tracking(Events.public_identity_buyer_item_paypal_pay_pressed())
    window.location.href = checkoutUrlForLoggedInUser
  }

  const advanceToGuestPayFlow = e => {
    e.preventDefault()
    tracking(Events.public_identity_buyer_item_guest_pay_pressed())
    window.location.href = checkoutUrlForGuest
  }

  const handleChat = e => {
    e.preventDefault()

    tracking(Events.public_identity_buyer_item_message_pressed())
  }

  const handleShare = e => {
    e.preventDefault()

    tracking(Events.public_identity_buyer_item_upload_pressed())
  }

  const shippingFeePrice =
    product.shippingDetails &&
    product.shippingDetails.type === 'FLAT_RATE' &&
    getFormattedAmount({
      amount: product.shippingDetails.cost.value,
      currencyCode: product.shippingDetails.cost.currencyCode,
      symbolIsoCurrency: true,
    })

  return (
    <Container data-test-id="shops-product-detail">
      <ProductCoverPhoto
        data-test-id="shops-product-photos-container"
        allowEdit={false}
        productPhotos={product && product.listingImages}
      />
      <ContentBlock>
        <HeadingText size="sm" className={`${ProductTitle}`}>
          {product && product.name}
        </HeadingText>
        <Divider theme="v2" className={`${DividerCss}`} />
        <ShippingFee>
          <V2ShippingSendIcon size="xs" />
          {product.shippingDetails &&
          product.shippingDetails.type === 'FLAT_RATE' ? (
            <CaptionText style={{ marginLeft: '6px', color: v2grey600 }}>
              {l10n('shippingFee', { shippingFeePrice })}
            </CaptionText>
          ) : (
            <CaptionText style={{ marginLeft: '6px', color: v2grey600 }}>
              {l10n('freeShipping')}
            </CaptionText>
          )}
        </ShippingFee>
        <ShopActions>
          <Button
            theme="v2"
            className={`${
              !isUserLoggedIn
                ? css(shopActionButton, customMarginRight)
                : css(shopActionButton)
            }`}
            onClick={advanceToPayWithPaypalFlow}
            data-test-id="shops-product-pay-with-paypal"
            branded
          >
            {l10n('button.payWithPayPal')}
          </Button>
          {!isUserLoggedIn && (
            <Button
              theme="v2"
              className={`${css(shopActionButton, customMarginLeft)}`}
              onClick={advanceToGuestPayFlow}
              data-test-id="shops-product-pay-as-guest"
            >
              {l10n('button.payAsAGuest')}
            </Button>
          )}
        </ShopActions>

        <ProductActions>
          <IconButton
            theme="v2"
            svgIconComponent={V2ChatIcon}
            onClick={handleChat}
            buttonType="secondary"
            className={`${productActionButton}`}
          >
            {l10n('icon.chat')}
          </IconButton>
          <IconButton
            theme="v2"
            svgIconComponent={V2ShareIosIcon}
            onClick={handleShare}
            buttonType="secondary"
            className={`${productActionButton}`}
          >
            {l10n('icon.share')}
          </IconButton>
        </ProductActions>
        <Divider theme="v2" className={`${DividerCss}`} />
        <BodyText className={`${ProductDescription}`} strong>
          {l10n('productDetails')}
        </BodyText>
        <BodyText style={{ whiteSpace: 'pre-line' }}>
          {product && product.description}
        </BodyText>
      </ContentBlock>
    </Container>
  )
}
export default ProductDetail
